import { agent } from '@shared/services/agent';

export async function updateCompany(company: any) {
    return await agent.requests.put('/api/company', company);
}

export async function updateCompanyName(companyName: string) {
    return await agent.requests.put('/api/company/name', {name: companyName});
}

export async function requestCompanyInfo() {
    return await agent.requests.get('/api/company-info')
}

export async function requestDomainNameSignUp(enabled: boolean) {
    return await agent.requests.post('/api/company/domain-name', { enabled })
}

export async function requestTenantIdSignUp(enabled: boolean, tenantId?: string) {
    return await agent.requests.post('/api/company/tenant-id', { enabled: enabled, tenantId: tenantId || null })
}

export async function updateCompanySetting(field: 'TENANT_ID' | 'SCHENGEN_TRACKING' | 'GLOBAL_REMINDER' | 'SHOW_BIRTHDAYS' | 'PRIVATE_GROUPS' | 'SCIM_DEPARTMENTS' | 'DISABLE_SCIM_WELCOME_EMAILS' | 'DISABLE_NEW_SCIM_USER_ACCESS' | 'RESTRICT_TEAM_MANAGEMENT' | 'NOTIFY_MANAGER_ON_LEAVE_CANCELLATION' | 'MCP_INTEGRATION' | 'CALENDAR_SYNC' | 'LOCKERS' | 'SIGNAGE' | 'SIGNAGE_NAME_DISPLAY' | 'SIGNAGE_SHOW_SAFETY_ROLES' | 'AUTOMATIC_OFFICE_CHECKIN', value: any) {
    return await agent.requests.post('/api/company/update', { field, value })
}

export type LockedSignupDomainStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface LockedSignupDomain {
    id: number;
    domain: string;
    status: LockedSignupDomainStatus;
    createdAt: string;
    decidedAt: string | null;
}

export async function listLockedSignupDomains(): Promise<LockedSignupDomain[]> {
    return await agent.requests.get('/api/company/locked-signup-domains');
}

export async function addLockedSignupDomain(domain: string): Promise<LockedSignupDomain> {
    return await agent.requests.post('/api/company/locked-signup-domains', { domain });
}

export async function removeLockedSignupDomain(id: number): Promise<void> {
    return await agent.requests.del(`/api/company/locked-signup-domains/${id}`);
}

export interface McpApprovedApp {
    id: number;
    redirectUri: string;
    host: string;
    clientName: string | null;
    approvedAt: string;
}

export async function listMcpApprovedApps(): Promise<McpApprovedApp[]> {
    return await agent.requests.get('/api/company/mcp-apps');
}

export async function removeMcpApprovedApp(id: number): Promise<void> {
    return await agent.requests.del(`/api/company/mcp-apps/${id}`);
}

export type SignupDomainStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SignupDomain {
    id: number;
    domain: string;
    status: SignupDomainStatus;
    createdAt: string;
    decidedAt: string | null;
}

export async function listSignupDomains(): Promise<SignupDomain[]> {
    return await agent.requests.get('/api/company/signup-domains');
}

export async function addSignupDomain(domain: string): Promise<SignupDomain> {
    return await agent.requests.post('/api/company/signup-domains', { domain });
}

export async function removeSignupDomain(id: number): Promise<void> {
    return await agent.requests.del(`/api/company/signup-domains/${id}`);
}

export async function getWhereaboutsReminderConfig(): Promise<WhereaboutsReminderConfig> {
    return await agent.requests.get('/api/whereabouts-reminder-config')
}

export async function updateWhereaboutsReminderConfig(config: WhereaboutsReminderConfig): Promise<any> {
    return await agent.requests.post('/api/whereabouts-reminder-config', config)
}

export interface WhereaboutsReminderConfig {
    enabled: boolean;
    dayOfWeek: any;
    hourOfDay: number;
    timezone: string | null;
}

export interface ApiKeyDetails {
    apiKey: string;
    label: string | null;
}

export interface ApiKeysResponse {
    publicApiKey: ApiKeyDetails | null;
    scimApiKeys: ApiKeyDetails[];
}

export async function getApiKeys(): Promise<ApiKeysResponse> {
    return await agent.requests.get('/api/company/api-keys');
}

export async function regeneratePublicApiKey(label: string | null): Promise<ApiKeyDetails> {
    return await agent.requests.post('/api/company/api-keys/public', { label });
}

export async function generateScimApiKey(label: string | null): Promise<ApiKeyDetails> {
    return await agent.requests.post('/api/company/api-keys/scim', { label });
}

export async function updatePublicApiKeyLabel(label: string | null): Promise<ApiKeyDetails> {
    return await agent.requests.put('/api/company/api-keys/public/label', { label });
}

export async function updateScimApiKeyLabel(apiKey: string, label: string | null): Promise<ApiKeyDetails> {
    return await agent.requests.put(`/api/company/api-keys/scim/label?apiKey=${encodeURIComponent(apiKey)}`, { label });
}

export async function revokePublicApiKey(): Promise<void> {
    return await agent.requests.del('/api/company/api-keys/public');
}

export async function revokeScimApiKey(apiKey: string): Promise<void> {
    return await agent.requests.del(`/api/company/api-keys/scim?apiKey=${encodeURIComponent(apiKey)}`);
}
